import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { SurfaceHeader } from "@-/dev.improve.haniverse.components.ui.surface";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { ActionMenu } from "@-/dev.improve.haniverse.components.ui.action-menu";
import { GraphemeDictionarySheetSurface } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview";
import { useMorphemeInfoPanelCompositionGraphemeSections } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.composition";
import { useMorphemeInfoPanelSentencesGraphemeSections } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.sentences";
import { DictionaryTopSheet } from "#dictionary-top-sheet";

/**
 * The closed sheet keeps the character's core details above its words, with the Compounds surface's
 * header (and count) peeking out beneath them. Swiping down reveals every compound.
 */
export function GraphemeDictionaryTopSheet({
  graphemeCard,
}: {
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}) {
  const compounds = useMorphemeInfoPanelCompositionGraphemeSections({ graphemeCard });
  const usage = useMorphemeInfoPanelSentencesGraphemeSections({ graphemeCard });
  return (
    <DictionaryTopSheet
      contextSections={usage}
      expandedContent={compounds.map((section) => (
        <View key={section.bodyKey}>{section.renderBody()}</View>
      ))}
      kind="grapheme"
      onEndReached={() => {
        for (const section of compounds) {
          section.onEndReached?.();
        }
      }}
      overviewContent={null}
      renderExpandedHeader={(expand) =>
        compounds.map((section) => (
          <SurfaceHeader
            className="flex-row items-center justify-between py-2"
            key={section.headerKey}
          >
            <Pressable
              accessibilityLabel="Reveal compounds"
              accessibilityRole="button"
              className="min-w-0 flex-1 flex-row items-center gap-x-2"
              onPress={expand}
            >
              <Text className="text-xs font-semibold">{section.title}</Text>
              {section.compoundCount === undefined ? null : (
                <Text
                  className="text-xs text-muted-foreground"
                  testID="grapheme-dictionary-compound-count"
                >
                  {section.compoundCount.toLocaleString()}
                </Text>
              )}
            </Pressable>
            {section.isLoading ? (
              <ActivityIndicator size="small" />
            ) : (
              <View className="flex-row items-center gap-1">
                {section.rareCompoundsFilter === undefined ? null : (
                  <ActionMenu
                    align="end"
                    items={[
                      {
                        checked: section.rareCompoundsFilter.isShowingRareCompounds,
                        kind: "checkbox",
                        label: "Show rare compounds",
                        onCheckedChange: (isShowingRareCompounds) => {
                          // Opening the sheet shows what the filter changed.
                          expand();
                          section.rareCompoundsFilter?.setIsShowingRareCompounds(
                            isShowingRareCompounds,
                          );
                        },
                        testID: "grapheme-dictionary-show-rare-compounds",
                      },
                    ]}
                    side="bottom"
                    sideOffset={4}
                    title="Filters"
                    trigger={
                      <Pressable
                        accessibilityLabel="Filter compounds"
                        accessibilityRole="button"
                        className="size-6 items-center justify-center"
                        hitSlop={6}
                        testID="grapheme-dictionary-compounds-filter"
                      >
                        <Icon
                          // Tinted while the filter widens the list beyond the default.
                          className={
                            section.rareCompoundsFilter.isShowingRareCompounds
                              ? "text-primary"
                              : "text-muted-foreground"
                          }
                          name="MdTune"
                          size={16}
                        />
                      </Pressable>
                    }
                    triggerHitSlop={6}
                  />
                )}
                <Pressable
                  accessibilityLabel="Toggle compound details"
                  accessibilityRole="button"
                  className="p-1"
                  hitSlop={6}
                  onPress={() => {
                    expand();
                    section.onToggle();
                  }}
                >
                  <Icon name={section.trailingIconName ?? "LuPlus"} size={16} />
                </Pressable>
              </View>
            )}
          </SurfaceHeader>
        ))
      }
      renderHeader={(onLayout) => (
        <View onLayout={onLayout}>
          <GraphemeDictionarySheetSurface className="mx-1 mb-1" graphemeCard={graphemeCard} />
        </View>
      )}
    />
  );
}
